/**
 * Atlas top-navigation configuration.
 *
 * Single source of truth for what appears in the desktop dropdowns and mobile
 * menu. Adding a new module is one entry here — no edits to Layout.tsx.
 *
 * Role gating: items with no `roles` field are visible to every authenticated
 * user. Items with `roles: [...]` are visible only when the user has at least
 * one of the listed roles. Use the constants from `@/auth/roles` rather than
 * raw strings.
 *
 * Section ordering and item ordering within sections both follow the array
 * order — the renderer does not re-sort.
 */

import { ROLES, ROLE_GROUPS } from '@/auth/roles';
import { SIX_BOX_FRONTEND_ALLOWLIST } from '@/auth/sixBoxAccess';

export interface NavItem {
  /** Stable id for keying — does not need to match path. */
  id: string;
  /** Route path the link navigates to. */
  to: string;
  /** Display label in the dropdown / mobile list. */
  label: string;
  /** Optional sub-text shown beneath the label in the desktop dropdown only. */
  description?: string;
  /**
   * Roles allowed to see this item. Omit (undefined) to show to all
   * authenticated users.
   */
  roles?: readonly string[];
  /**
   * Optional email allowlist (case-insensitive) — item is visible when the
   * user matches the allowlist regardless of role. Used for time-boxed pilot
   * gating before a dedicated role is provisioned tenant-wide.
   */
  emailAllowlist?: readonly string[];
}

export interface NavSection {
  /** Stable id for keying. */
  id: string;
  /** Section header text (UPPERCASE in the rendered nav). */
  title: string;
  items: NavItem[];
  /**
   * Optional roles required to see this whole section (e.g. Admin section
   * only visible to admins). When omitted, section visibility is determined
   * by item-level roles only.
   */
  roles?: readonly string[];
  /**
   * Optional: a feature the person must have, decided by a server answer
   * rather than a role. `'intake'` is Product Ideas: shown only when intake's
   * `/me` gives the person a role (ATLAS-107a, D-112). Pass the answer to
   * getNavigationForRoles; a section is dropped when it isn't met.
   */
  requires?: 'intake';
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'company',
    title: 'Company',
    items: [
      { id: 'about', to: '/about', label: 'About Us', description: 'Our story, locations & accreditations' },
      { id: 'about-teemee', to: '/about/teemee', label: 'About TeeMee', description: 'The origin story of the JMTS mascot' },
      { id: 'strategy', to: '/strategy', label: 'Strategy', description: 'Company vision & strategic pillars' },
      { id: 'town-halls', to: '/town-halls', label: 'Town Halls', description: 'Upcoming & past town halls' },
      { id: 'executives', to: '/executives', label: 'Executive Team', description: 'Leadership profiles' },
      { id: 'ask-andrew', to: '/ask-andrew', label: 'Ask Andrew', description: 'AI-powered Q&A' },
    ],
  },
  {
    // ATLAS-107a (D-39, D-24): only the intake pilot list sees it, until HR agrees to a new top-level section.
    // Two items on purpose: with one, the header renders the section as a bare link (prototype UX review 3).
    id: 'improve',
    title: 'Improve',
    requires: 'intake',
    items: [
      { id: 'product-ideas', to: '/intake', label: 'Product Ideas', description: 'Ideas from across JMTS, and how they are doing' },
      { id: 'submit-idea', to: '/intake/new', label: 'Submit an idea', description: 'Tell us what gets in your way' },
    ],
  },
  {
    id: 'grow',
    title: 'Grow',
    items: [
      { id: 'analytics', to: '/analytics', label: 'Analytics', description: 'Reports & dashboards' },
      { id: 'kpis', to: '/kpis', label: 'KPI Tracker', description: 'Goals & performance metrics' },
      {
        id: 'tech-progression',
        to: '/tech-progression',
        label: 'Tech Progression',
        description: 'Technician level assessments',
        roles: [ROLES.ADMIN, ROLES.TECH_PROGRESSION, ROLES.MANAGER, ...ROLE_GROUPS.TECH_PROGRESSION_ADMIN],
      },
      {
        id: 'tech-progression-admin',
        to: '/tech-progression/admin',
        label: 'Tech Progression — Admin',
        description: 'HR master view of all submissions',
        roles: [...ROLE_GROUPS.TECH_PROGRESSION_ADMIN],
      },
      { id: 'people', to: '/people', label: '1:1 Connect', description: 'AI-powered 1:1 prep & history' },
      {
        id: 'six-box',
        to: '/six-box',
        label: '6-Box Talent',
        description: 'Quarterly performance × trajectory grid (HR pilot)',
        roles: [ROLES.SIX_BOX, ROLES.ADMIN],
        emailAllowlist: SIX_BOX_FRONTEND_ALLOWLIST,
      },
      { id: 'documents', to: '/documents', label: 'Documents', description: 'Company document library' },
    ],
  },
  {
    id: 'admin',
    title: 'Admin',
    roles: [ROLES.ADMIN],
    items: [
      { id: 'admin-panel', to: '/admin', label: 'Admin Panel', description: 'Platform-level controls' },
    ],
  },
];

/**
 * Returns the nav sections + items the user is allowed to see.
 *
 * - Sections with their own `roles` requirement are dropped entirely if the
 *   user's role set doesn't intersect.
 * - Within a visible section, items with `roles` are filtered the same way,
 *   except items with an `emailAllowlist` are also visible when the user's
 *   email matches the allowlist (case-insensitive) — used for the 6-Box
 *   pilot gating until a dedicated role is provisioned.
 * - Sections with no remaining visible items are dropped.
 * - A section with `requires` is dropped unless `available` says so; leaving
 *   `available` out changes nothing else (ATLAS-107a).
 */
export function getNavigationForRoles(
  userRoles: readonly string[],
  userEmail?: string,
  available: { intake?: boolean } = {},
): NavSection[] {
  const has = (required: readonly string[]): boolean =>
    required.some((r) => userRoles.includes(r));
  const emailLower = userEmail?.toLowerCase();
  const inAllowlist = (allowlist?: readonly string[]): boolean =>
    !!emailLower && !!allowlist && allowlist.some((e) => e.toLowerCase() === emailLower);

  return NAV_SECTIONS.flatMap((section) => {
    if (section.roles && !has(section.roles)) return [];
    if (section.requires && !available[section.requires]) return [];

    const visibleItems = section.items.filter((item) => {
      if (!item.roles && !item.emailAllowlist) return true;
      if (item.roles && has(item.roles)) return true;
      if (item.emailAllowlist && inAllowlist(item.emailAllowlist)) return true;
      return false;
    });

    if (visibleItems.length === 0) return [];

    return [{ ...section, items: visibleItems }];
  });
}
